/**
 * Server-side WebSocket subscription limits mirrored from
 * backend/cmd/listeners/ws-handler/main.go (MaxSubsPerRequest, MaxSubsPerConnection, MaxPatternLen).
 *
 * Consumed here for proactive dev-mode assertions and will be imported by
 * ENG-3111 follow-up work.
 */

/** Maximum number of subscriptions allowed in a single subscribe request. */
export const MAX_SUBS_PER_REQUEST = 50;

/**
 * Maximum total active subscriptions per WebSocket connection.
 *
 * Kept as a module-local constant (not exported) because no consumer currently
 * imports it — exporting an unused symbol triggers knip/unused-export warnings.
 * Mirrors backend ws-handler/main.go:21 (MaxSubsPerConnection = 100) for
 * documentation purposes. Export when ENG-3111 follow-up adds a consumer.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _MAX_SUBS_PER_CONNECTION = 100;

/** Maximum byte length of a subscription pattern. */
export const MAX_PATTERN_LEN = 256;

/**
 * Lifetime cap for the unsubscribe-before-ack late-ack one-shot listener
 * (useRealtimeSubscription cleanup path).
 *
 * The late-ack listener is added to the singleton WebSocket `handlers` Set,
 * which is NEVER cleared on socket close/reconnect (useWebSocket.ts). If the
 * `subscribed` ack for its requestId never arrives — the documented P0 trigger
 * where a backgrounded tab drops the socket (1006) mid-subscribe and the lost
 * requestId is never echoed (reconnect mints new ids) — the listener would
 * otherwise stay resident for the life of the page and run its predicate on
 * every WS message, an unbounded per-session leak. This bound forces a
 * self-expiry so each orphaned listener lives at most this long.
 *
 * 10s is comfortably past a normal WS subscribe round trip; if the ack hasn't
 * landed by then it is not coming (the connection that would deliver it is gone
 * and $disconnect has already cleaned up the subscriptions server-side).
 */
export const LATE_ACK_TIMEOUT_MS = 10_000;
